/**
 * Toolbar — top-of-canvas horizontal tool strip (T-1.1.5 + T-1.3.6).
 *
 * Story 1.1 / T-1.1.5:
 *   - Render a single "重置视角" button that calls `onResetView`.
 *   - Render the M2 placeholder text so users see scope.
 *
 * Story 1.3 / T-1.3.6 (this revision):
 *   - Add a "放置" tool button. The MVP default tool is 'place' — every
 *     click on the canvas places a voxel (T-1.3.6 wires the click
 *     handler in MainCanvas; the toolbar just exposes the visual
 *     affordance + active-tool highlight).
 *   - The active tool is read from `useSceneStore` (FR-305..307). When
 *     state.ui.tool === 'place' the "放置" button is highlighted.
 *   - Other tools (move / select / paint / label / unfold) are
 *     placeholders until M2 lands — they render as disabled buttons so
 *     the user can see the shape of the toolbar without false affordances.
 *
 * Why the toolbar reads the store directly (not via a prop):
 *   - The active-tool highlight is the *only* piece of toolbar UI that
 *     needs to react to store changes. Funneling it through a prop
 *     would force App.tsx to subscribe + pass the slice on every render
 *     — the store subscription is local to this component.
 *
 * Why the placeholder buttons are still rendered (not hidden):
 *   - SPEC §6 demands the toolbar surface show its full M2 layout in
 *     a "preview" state from M1 onwards, so the layout doesn't shift
 *     when M2 lands. Disabled is the cheapest way to lock the shape.
 */

import { useSceneStore } from '@/state/sceneStore';

export interface ToolbarProps {
  /** Invoked once when the user clicks the "重置视角" button. */
  onResetView: () => void;
}

/** M2 tool labels — placeholder set, kept here for the disabled buttons. */
const TOOL_PLACEHOLDERS: Array<{ tool: string; label: string }> = [
  { tool: 'move', label: '移动' },
  { tool: 'select', label: '选中' },
  { tool: 'paint', label: '涂色' },
  { tool: 'label', label: '标号' },
  { tool: 'unfold', label: '展开' },
];

export function Toolbar({ onResetView }: ToolbarProps): JSX.Element {
  const activeTool = useSceneStore((s) => s.ui.tool);
  const isPlace = activeTool === 'place';

  return (
    <div
      role="toolbar"
      aria-label="工具栏"
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 10,
        display: 'flex',
        alignItems: 'center',
        gap: '0.75rem',
        padding: '0.5rem 0.75rem',
        background: 'rgba(17, 17, 17, 0.85)',
        borderBottom: '1px solid #222',
        backdropFilter: 'blur(4px)',
        // T-1.5 / Bug-001: Toolbar 外层 div 全宽 (top:0 left:0 right:0 z-index:10)
        // 没有 pointer-events:none 会吃掉画布顶部 51.78px × 全宽 的所有
        // drag/drop 事件 (实测 elementFromPoint 确认). 改为 none 让事件穿透
        // 到 MainCanvas wrapper div, 内部 button 单独 'auto' 保持可点.
        // 模式跟 voxel-count (data-testid=voxel-count) 一致.
        pointerEvents: 'none',
      }}
    >
      <span
        style={{
          fontSize: '0.85rem',
          color: '#888',
          userSelect: 'none',
          pointerEvents: 'none',
        }}
      >
        工具栏 — M2 完整实现
      </span>
      {/* T-1.3.6 — the active tool. Highlighted when state.ui.tool === 'place'. */}
      <button
        type="button"
        aria-pressed={isPlace}
        data-tool="place"
        style={{
          padding: '0.4rem 0.85rem',
          fontSize: '0.85rem',
          background: isPlace ? '#1f6feb' : '#262626',
          color: '#fff',
          border: isPlace ? '1px solid #1a5fcc' : '1px solid #333',
          borderRadius: '4px',
          cursor: 'pointer',
          fontWeight: isPlace ? 600 : 400,
          // 按钮单独恢复 pointer-events, 保持可点击.
          pointerEvents: 'auto',
        }}
      >
        放置
      </button>
      {/* M2 placeholder buttons — locked shape, disabled until M2 lands. */}
      {TOOL_PLACEHOLDERS.map(({ tool, label }) => (
        <button
          key={tool}
          type="button"
          disabled
          data-tool={tool}
          title={`${label} (M2 实现)`}
          style={{
            padding: '0.4rem 0.85rem',
            fontSize: '0.85rem',
            background: '#1a1a1a',
            color: '#666',
            border: '1px solid #262626',
            borderRadius: '4px',
            cursor: 'not-allowed',
            // placeholder 按钮也单独 auto, 即便 disabled 也保持一致性.
            pointerEvents: 'auto',
          }}
        >
          {label}
        </button>
      ))}
      <button
        type="button"
        onClick={onResetView}
        style={{
          padding: '0.4rem 0.85rem',
          fontSize: '0.85rem',
          background: '#1f6feb',
          color: '#fff',
          border: '1px solid #1a5fcc',
          borderRadius: '4px',
          cursor: 'pointer',
          // "重置视角" 按钮单独 auto, 保持可点.
          pointerEvents: 'auto',
        }}
      >
        重置视角
      </button>
    </div>
  );
}
